import { Stack } from "@mui/material";
import { LightbulbIcon } from "@truevault/aquilo/src/components/selectMenu/SelectMenu";
import React, { useMemo } from "react";
import {
  disclosedRecommendationsForDataRecipient,
  recommendationsForDataRecipient,
} from "../../../common/service/resources/data-map/dataMapHelpers";
import { useDataMapCollectedPIC } from "../../../common/service/resources/data-map/dataMapTransformers";
import { CollectionGroupDetailsDto } from "../../../common/service/server/dto/CollectionGroupDto";
import { DataMapDto, PICGroup } from "../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UUIDString } from "../../../common/service/server/Types";
import { InlineLoading } from "../../../common/components/Loading";
import { UpdateDisclosedArgs } from "../../hooks/useDisclosuresForCollectionGroups";
import { DESCRIPTION_OVERRIDES } from "../../pages/get-compliant/data-recipients/DataRecipientCollectionCheckboxes";
import { DescriptionCheckbox } from "../input/DescriptionCheckbox";
import { RecipientGuidance } from "./data-recipients/RecipientGuidance";

type DataRecipientDisclosureCheckboxesProps = {
  updateDisclosure: (params: UpdateDisclosedArgs) => void;
  collectionGroups: CollectionGroupDetailsDto[];
  collectedPicGroups: PICGroup[];
  selected: UUIDString[];
  pending: UUIDString[];
  showRecommendations?: boolean;
  dataMap?: DataMapDto | undefined;
  dataRecipient: OrganizationDataRecipientDto;
  showCollectedRecommendations?: boolean;
  showSensitiveCategories?: boolean;
  useNewDesign?: boolean;
};

export const DataRecipientDisclosureCheckboxes: React.FC<
  DataRecipientDisclosureCheckboxesProps
> = ({
  updateDisclosure,
  dataMap,
  collectionGroups,
  collectedPicGroups,
  selected,
  pending,
  dataRecipient,
  showRecommendations = false,
  showCollectedRecommendations = false,
  showSensitiveCategories = false,
  useNewDesign = false,
}) => {
  const { recommendedGroups } = useMemo(() => {
    if (dataMap && showRecommendations) {
      return recommendationsForDataRecipient(dataMap, dataRecipient);
    } else if (dataMap && showCollectedRecommendations) {
      return disclosedRecommendationsForDataRecipient(dataMap, dataRecipient, collectionGroups);
    }
    return { recommendedGroups: [] };
  }, [dataMap, dataRecipient, showRecommendations, showCollectedRecommendations]);

  const handleOnChange = (disclosedId: UUIDString, disclosed: boolean) => {
    updateDisclosure({
      vendorId: dataRecipient.vendorId,
      disclosedGroupIds: [disclosedId],
      disclosed,
    });
  };

  if (useNewDesign) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
        {collectedPicGroups.map((picGroup) => (
          <NewDesignCheckbox
            key={picGroup.id}
            dataMap={dataMap}
            collectionGroups={collectionGroups}
            picGroup={picGroup}
            selected={selected}
            pending={pending}
            recommendedGroups={recommendedGroups}
            onChange={handleOnChange}
            showSensitiveCategories={showSensitiveCategories}
          />
        ))}
      </div>
    );
  }

  return (
    <>
      <RecipientGuidance recipient={dataRecipient} />
      <Stack spacing={2}>
        {collectedPicGroups.map((picGroup) => (
          <DataRecipientDisclosureCheckbox
            key={picGroup.id}
            dataMap={dataMap}
            collectionGroups={collectionGroups}
            picGroup={picGroup}
            selected={selected}
            pending={pending}
            recommendedGroups={recommendedGroups}
            onChange={handleOnChange}
            showSensitiveCategories={showSensitiveCategories}
          />
        ))}
      </Stack>
    </>
  );
};

export type CheckboxItemProps = {
  dataMap: DataMapDto | undefined;
  collectionGroups: CollectionGroupDetailsDto[];
  picGroup: PICGroup;
  selected: UUIDString[];
  pending: UUIDString[];
  recommendedGroups: PICGroup[];
  onChange: (disclosedId: UUIDString, disclosed: boolean) => void;
  showSensitiveCategories?: boolean;
};

export function useCheckboxItemData({
  dataMap,
  collectionGroups,
  picGroup,
  selected,
  pending,
  recommendedGroups,
  showSensitiveCategories = false,
}: Omit<CheckboxItemProps, "onChange">) {
  const cgIds = collectionGroups.map((cg) => cg.id);
  const collectedPic = useDataMapCollectedPIC(dataMap, {
    collectionGroupId: cgIds,
    picGroupId: picGroup.id,
  });
  const collectedLabel = collectedPic.map((c) => c.name).join(", ");
  const containsSensitivePic = collectedPic.some((c) => c.sensitiveRegions?.length > 0);
  const description = DESCRIPTION_OVERRIDES[picGroup.key] ?? `You collect: ${collectedLabel}`;
  const recommended = recommendedGroups?.some((rg) => rg.id === picGroup.id);
  const isChecked = selected?.includes(picGroup.id);
  const isLoading = pending?.includes(picGroup.id);

  return {
    description,
    recommended,
    isChecked,
    isLoading,
    containsSensitivePic,
    showSensitiveCategories,
  };
}

const NewDesignCheckbox: React.FC<CheckboxItemProps> = (props) => {
  const { picGroup, onChange } = props;
  const {
    description,
    recommended,
    isChecked,
    isLoading,
    containsSensitivePic,
    showSensitiveCategories,
  } = useCheckboxItemData(props);

  return (
    <label className="flex items-start gap-3 px-5 py-4 cursor-pointer">
      <input
        type="checkbox"
        checked={isChecked}
        disabled={isLoading}
        onChange={(e) => onChange(picGroup.id, e.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
      />
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-gray-900">{picGroup.name}</span>
          {showSensitiveCategories && containsSensitivePic && (
            <span className="text-red-500 font-bold">*</span>
          )}
          {recommended && <span className="inline-flex shrink-0">{LightbulbIcon}</span>}
          {isLoading && (
            <span className="ml-1">
              <InlineLoading size="sm" />
            </span>
          )}
        </div>
        {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
      </div>
    </label>
  );
};

const DataRecipientDisclosureCheckbox: React.FC<CheckboxItemProps> = (props) => {
  const { picGroup, onChange } = props;
  const {
    description,
    recommended,
    isChecked,
    isLoading,
    containsSensitivePic,
    showSensitiveCategories,
  } = useCheckboxItemData(props);

  return (
    <DescriptionCheckbox
      label={
        <>
          {picGroup.name}
          {showSensitiveCategories && containsSensitivePic && (
            <span className="ml-xs" style={{ color: "#d32f2f", fontWeight: "bold" }}>
              *
            </span>
          )}
        </>
      }
      description={description}
      checked={isChecked}
      variant={recommended ? "light-purple" : undefined}
      loading={isLoading}
      onChange={(_, checked) => onChange(picGroup.id, checked)}
    />
  );
};
